import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  HostListener,
  AfterViewInit,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog'; // <-- ADDED
import { trigger, state, style, transition, animate } from '@angular/animations';
import { DialogComponent } from '../dialog/confirm.dialog';

declare var MediaRecorder: any;

@Component({
  selector: 'app-camera',
  templateUrl: './camera.component.html',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatRippleModule,
    MatTooltipModule
  ],
  styleUrls: ['./camera.component.scss'],
  animations: [
    trigger('fadeInOut', [
      state('void', style({ opacity: 0 })),
      transition('void <=> *', animate(300))
    ])
  ]
})
export class CameraComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('video') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') canvasElement!: ElementRef<HTMLCanvasElement>;
  @ViewChild('countdown') countdownElement!: ElementRef<HTMLDivElement>;

  @Output() photoCaptured = new EventEmitter<any>();
  @Output() videoCaptured = new EventEmitter<any>();

  isVideoMode = false;
  mediaStream!: MediaStream;
  isRecording = false;
  mediaRecorder!: MediaRecorder;
  recordedChunks: Blob[] = [];
  
  // Camera settings
  videoMimeTypes: string[] = [
    'video/mp4',
    'video/webm',
    'video/mpeg',
    'video/ogg',
    'video/quicktime',
  ];
  recordableMimeType: string = '';
  facingMode: string = 'user'; // Default to front camera
  
  // Slightly lower quality settings
  imageQuality: number = 0.8; // 80% JPEG quality
  videoQuality: number = 0.6; // 60% quality for video
  
  // UI state
  showControls = true;
  countdown = 0;
  recordingTime = 0;
  recordingTimer?: any;
  flashActive = false;
  captureInProgress = false;
  
  // File size management
  targetMaxFileSize = 600 * 1024; // 600KB limit
  estimatedFileSize = 0;          // Current estimated file size
  fileSizeWarning = false;        // Warning flag
  showSizeFeedback = true;        // Always show size feedback during recording
  
  // Fake size generation parameters - designed to hit ~580KB at 20 seconds
  fakeSizeIncrement = 29 * 1024;  // 29KB per second (580KB / 20 seconds)
  
  // Tracking for real file size
  actualFileSize = 0;             // Actual measured file size
  useActualSize = false;          // Whether to use the actual size vs. fake size
  
  // If user goes over limit, this will be true
  overSizeLimit = false;

  // Add a flag to track if the camera is ready
  isCameraReady = false;

  // Guard to indicate the user chose to cancel (discard) during recording
  private isCancelling = false; // <-- ADDED

  constructor(
    private dialogRef: MatDialogRef<CameraComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private matDialog: MatDialog, // <-- ADDED
  ) {}

// AFTER
async ngOnInit() {
  this.dialogRef.disableClose = true;
  this.dialogRef.backdropClick().subscribe(() => {
    this.onAttemptDismiss('backdrop');
  });
  this.dialogRef.keydownEvents().subscribe((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.onAttemptDismiss('escape');
    }
  });
}

async ngAfterViewInit() {
  try {
    await this.initCamera();
  } catch (error) {
    console.error('Error accessing camera:', error);
  }
}

  // Centralized close with confirmation if recording or capturing
  private async onAttemptDismiss(source: 'backdrop' | 'escape' | 'button') { // <-- ADDED
    if (this.isRecording || this.captureInProgress) {
      const msg = this.isVideoMode
        ? 'Stop and discard the current recording?'
        : 'Discard the current capture?';
      const confirmed = await this.openConfirmDialog('Recording in progress', msg);
      if (!confirmed) return;

      // User confirmed discard
      this.isCancelling = true;
      if (this.isRecording && this.mediaRecorder?.state === 'recording') {
        try { this.mediaRecorder.stop(); } catch {}
      }
      this.cleanupTimers();
      this.dialogRef.close(null);
      return;
    }

    // Not recording/capturing: close immediately
    this.cleanupTimers();
    this.dialogRef.close();
  }

  // Use shared DialogComponent to confirm
  private openConfirmDialog(header: string, content: string): Promise<boolean> { // <-- ADDED
    const ref = this.matDialog.open(DialogComponent, {
      data: { header, content, type: 'warning', buttonName: 'Discard' }
    });
    return ref.afterClosed().toPromise();
  }

  private cleanupTimers() { // <-- ADDED
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = undefined;
    }
  }

  async initCamera(deviceId?: string) {
    // Moderate quality settings for better file size
    const constraints: MediaStreamConstraints = {
      video: {
        width: { ideal: 640 },  // Standard width
        height: { ideal: 480 }, // Standard 4:3 aspect ratio
        facingMode: this.facingMode,
        frameRate: { ideal: 25 } // Reduced framerate
      },
      audio: this.isVideoMode ? {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 32000, // Reduced from 44100
        channelCount: 1    // Mono audio for efficiency
      } : false
    };
    
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
    }

    this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
    this.startVideo();
  }

  startVideo() {
    const video = this.videoElement.nativeElement;
    video.srcObject = this.mediaStream;
    video.muted = true; // Mute the audio during preview
    
    // Set flag when video is actually ready to capture
    video.onloadedmetadata = () => {
      video.play().then(() => {
        this.isCameraReady = true;
      }).catch(error => {
        console.error('Error playing video:', error);
      });
    };
  }

  capturePhoto(useCountdown = false) {
    if (this.captureInProgress) return;
    
    // Check if camera is ready
    if (!this.isCameraReady) {
      console.warn('Camera not ready yet, please wait a moment');
      return;
    }
    
    if (useCountdown && !this.countdown) {
      this.captureInProgress = true;
      this.countdown = 3;
      const countdownInterval = setInterval(() => {
        this.countdown--;
        if (this.countdown === 0) {
          clearInterval(countdownInterval);
          this.takePhoto();
          this.captureInProgress = false;
        }
      }, 1000);
    } else {
      this.captureInProgress = true;
      this.takePhoto();
      this.captureInProgress = false;
    }
  }

  takePhoto() {
    if (!this.isCameraReady) {
      console.warn('Camera not ready yet, cannot take photo');
      return;
    }
    
    // Briefly show flash effect
    this.flashActive = true;
    setTimeout(() => this.flashActive = false, 150);
    
    const video = this.videoElement.nativeElement;
    const canvas = this.canvasElement.nativeElement;

    if (video.videoWidth === 0 || video.videoHeight === 0) {
      console.error('Video dimensions not available yet');
      return;
    }

    const scaleFactor = 1.0;
    canvas.width = video.videoWidth * scaleFactor;
    canvas.height = video.videoHeight * scaleFactor;
    
    const context = canvas.getContext('2d');
    
    if (context) {
      if (this.facingMode === 'user') {
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
      }
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      try {
        const photoData = canvas.toDataURL('image/jpeg', this.imageQuality);
        if (!photoData || !photoData.startsWith('data:image/jpeg;base64,')) {
          console.error('Invalid photo data generated');
          return;
        }
        const responsePhoto = {
          base64: photoData,
          type: 'image/jpeg',
        };
        this.photoCaptured.emit(responsePhoto);
        this.dialogRef.close(photoData);
      } catch (error) {
        console.error('Error generating photo data:', error);
      }
    }
  }

  toggleVideoRecording() {
    if (!this.isCameraReady) {
      console.warn('Camera not ready yet, please wait a moment');
      return;
    }
    
    const mp4MimeType = 'video/mp4';
    if (MediaRecorder.isTypeSupported(mp4MimeType)) {
      this.recordableMimeType = mp4MimeType;
    } else {
      this.recordableMimeType = this.videoMimeTypes.find(
        (mimeType) => MediaRecorder.isTypeSupported(mimeType)
      ) || '';
      if (!this.recordableMimeType) {
        console.error('No supported video format found.');
        return;
      }
    }
  
    if (this.isRecording) {
      // Stop the recording
      this.mediaRecorder.stop();
      this.isRecording = false;
      this.cleanupTimers();
    } else {
      // Reset file size tracking
      this.estimatedFileSize = 0;
      this.fileSizeWarning = false;
      this.overSizeLimit = false;
      
      // Start the recording
      this.recordedChunks = [];
      
      interface MediaRecorderOptions {
        mimeType?: string;
        videoBitsPerSecond?: number;
        audioBitsPerSecond?: number;
      }
      
      const fullOptions: MediaRecorderOptions = {
        mimeType: this.recordableMimeType,
        videoBitsPerSecond: 240000,
        audioBitsPerSecond: 32000,
      };
      
      try {
        this.mediaRecorder = new MediaRecorder(this.mediaStream, fullOptions);
      } catch (e) {
        try {
          const mimeTypeOptions: MediaRecorderOptions = { mimeType: this.recordableMimeType };
          this.mediaRecorder = new MediaRecorder(this.mediaStream, mimeTypeOptions);
        } catch (e2) {
          this.mediaRecorder = new MediaRecorder(this.mediaStream);
        }
      }
      
      this.mediaRecorder.ondataavailable = (event: any) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
          this.actualFileSize = this.recordedChunks.reduce((sum, chunk) => sum + chunk.size, 0);
        }
      };
  
      this.mediaRecorder.onstop = () => {
        // If user canceled via backdrop/Esc confirmation, do not emit a file
        if (this.isCancelling) {
          this.isCancelling = false;
          this.isRecording = false;
          return;
        }

        if (this.recordedChunks.length === 0) {
          console.error('No recorded chunks available');
          this.dialogRef.close(null);
          return;
        }
        
        try {
          const videoBlob = new Blob(this.recordedChunks, { type: 'video/mp4' });
          if (videoBlob.size > this.targetMaxFileSize) {
            console.warn(`Video exceeds target size: ${videoBlob.size / 1024}KB vs ${this.targetMaxFileSize / 1024}KB limit`);
          }
          this.videoCaptured.emit(videoBlob);
          this.dialogRef.close(videoBlob);
        } catch (error) {
          console.error('Error finalizing video:', error);
          this.dialogRef.close(null);
        } finally {
          this.isRecording = false;
        }
      };
  
      this.mediaRecorder.start(500);
      this.isRecording = true;
      this.recordingTime = 0;
      
      this.recordingTimer = setInterval(() => {
        this.recordingTime++;
        this.estimatedFileSize = Math.max(
          this.recordingTime * this.fakeSizeIncrement,
          this.actualFileSize
        );
        this.useActualSize = this.actualFileSize > this.recordingTime * this.fakeSizeIncrement;
        
        if (this.data?.isSms) {
          this.fileSizeWarning = this.estimatedFileSize > (this.targetMaxFileSize * 0.8);
          this.overSizeLimit = this.estimatedFileSize > this.targetMaxFileSize;
        } else {
          this.fileSizeWarning = false;
          this.overSizeLimit = false;
        }
      }, 1000);
    }
  }

  async switchCamera() {
    this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
    this.isCameraReady = false;
  
    try {
      await this.initCamera();
    } catch (error) {
      console.error('Error switching camera:', error);
      this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
      try { await this.initCamera(); } catch {}
    }
  }

  async switchMode() {
    if (this.isRecording) return;
    this.isVideoMode = !this.isVideoMode;
    this.isCameraReady = false;
  
    try {
      await this.initCamera();
    } catch (error: any) {
      console.error('Error switching mode:', error);
  
      if (this.isVideoMode && error?.name === 'NotAllowedError') {
        // Mic denied — retry without audio so video still works (muted)
        try {
          await this.initCameraWithoutAudio();
        } catch (fallbackError) {
          this.isVideoMode = false;
          try { await this.initCamera(); } catch {}
        }
      } else {
        this.isVideoMode = !this.isVideoMode;
        try { await this.initCamera(); } catch {}
      }
    }
  }

  // Timer reference for auto-hiding controls
  private controlsTimeoutId?: number;

  @HostListener('mousemove')
  @HostListener('touchstart')
  onUserInteraction() {
    this.showControls = true;
    if (this.controlsTimeoutId) {
      clearTimeout(this.controlsTimeoutId);
    }
    this.setHideControlsTimeout();
  }

  setHideControlsTimeout() {
    this.controlsTimeoutId = window.setTimeout(() => {
      if (!this.isRecording) {
        this.showControls = false;
      }
    }, 3000);
  }

  closeDialog() {
    // Use the same guarded path as backdrop/Esc
    this.onAttemptDismiss('button');
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  
  formatFileSize(bytes: number): string {
    if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} KB`;
    } else {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
  }

  ngOnDestroy() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
    }
    this.cleanupTimers();
    if (this.controlsTimeoutId) {
      clearTimeout(this.controlsTimeoutId);
    }
  }

  private async initCameraWithoutAudio(): Promise<void> {
    const constraints: MediaStreamConstraints = {
      video: {
        width: { ideal: 640 },
        height: { ideal: 480 },
        facingMode: this.facingMode,
        frameRate: { ideal: 25 }
      },
      audio: false
    };
  
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
    }
  
    this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
    this.startVideo();
  }
}
